import { NodeArtifactsConstant } from '@/components/NodeArtifact';
import { NodePlaceholderConstant } from '@/components/NodePlaceholder';
import { UIModel } from '@/core/UIModel';
import * as Api from '@/types/ApiTypes';

export enum NodeType {
  ARTIFACTS = 'ARTIFACTS',
  PROMPT = 'PROMPT',
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO',
  THREE_D = '3D',
  PLACEHOLDER = 'PLACEHOLDER',
  PALETTE = 'PALETTE',
  DRAWING = 'DRAWING',
}

export enum NodeItemGroup {
  RESEARCH = 'Research',
  GENERATE = 'Generate',
  IMPORT = 'Import',
}

export enum NodePaletteType {
  Color = 'Color',
  Material = 'Material',
  Model = 'Model',
  Style = 'Style',
  CameraView = 'Camera view',
  ETC = 'ETC',
}
export class NodeItem extends UIModel {
  project_id: number;
  artifact_id: number;
  name: string;
  artifact: string;
  type: NodeType;
  x: number;
  y: number;
  visible: boolean;
  tags: string[];
  target: number[];
  source: number[];
  model: string;
  prompt: string;
  text: string; // palette에서는 palette 타입으로 사용
  content: string; // url
  state: 'idle' | 'loading' | 'error' | 'revoked';
  // image option
  sizeText: string;
  background: string;
  raw: boolean;
  dominantColor: { top: string; bottom: string; all: string };
  // drawing
  imageStyle: string;

  // image, drawing
  guidance: number;
  strength: number;

  // 3d, palette
  screenshot?: string;
  camera?: { azimuth: number; polar: number };

  // 3d
  textureMesh: boolean;
  material: 'PBR' | 'Shaded';
  tier: 'Regular' | 'Sketch';
  topology: 'Triangle' | 'Quad';

  // 임시 필드, DB에 저장 안함
  count: number;
  quality: string;
  size: string;
  duration: number;

  abortController?: AbortController;

  constructor() {
    super();
    this.name = '';
    this.project_id = 0;
    this.artifact_id = 0;
    this.artifact = '';
    this.type = NodeType.ARTIFACTS;
    this.x = 0;
    this.y = 0;
    this.prompt = '';
    this.text = '';
    this.content = '';
    this.state = 'idle';
    this.visible = false;
    this.tags = [];
    this.target = [];
    this.source = [];
    this.sizeText = '3:4';
    this.background = 'Auto';
    this.raw = false;
    this.model = '';
    this.count = 1;
    this.quality = '';
    this.duration = 5;
    this.size = '';
    this.dominantColor = { top: '', bottom: '', all: '' };

    this.material = 'PBR';
    this.tier = 'Regular';
    this.textureMesh = false;
    this.topology = 'Triangle';

    this.guidance = 1.5; // image default 3.5, drawing default 1.5
    this.strength = 0.75; // image default 0.1, drawing default 0.75
    this.imageStyle = 'NONE';
  }

  static parse(data: NodeItem) {
    const item = new NodeItem();
    item.id = data.id;
    item.project_id = data.project_id;
    item.artifact_id = data.artifact_id;
    item.name = data.name;
    item.artifact = data.artifact;
    item.type = data.type;
    item.x = data.x;
    item.y = data.y;
    item.visible = data.visible;
    item.tags = data.tags;
    item.target = data.target;
    item.source = data.source;
    item.text = data.text;
    item.state = data.state;
    item.content = data.content;
    item.prompt = data.prompt;
    item.sizeText = data.sizeText;
    item.background = data.background;
    item.raw = data.raw;
    item.model = data.model;
    item.count = data.count;
    item.quality = data.quality;
    item.size = data.size;
    item.dominantColor = data.dominantColor;
    item.abortController = data.abortController;
    item.screenshot = data.screenshot;
    item.camera = data.camera;
    item.duration = data.duration;
    item.guidance = data.guidance;
    item.strength = data.strength;
    item.textureMesh = data.textureMesh;
    item.material = data.material;
    item.tier = data.tier;
    item.topology = data.topology;
    return item;
  }

  getSidemenuGroup() {
    if (this.type === NodeType.ARTIFACTS) {
      return NodeItemGroup.RESEARCH;
    } else {
      return NodeItemGroup.GENERATE;
    }
  }

  setType(type: string) {
    if (type === NodeType.PROMPT) {
      this.type = NodeType.PROMPT;
      this.name = 'Prompt';
    } else if (type === NodeType.IMAGE) {
      this.type = NodeType.IMAGE;
      this.name = 'Image';
      this.model = '';
      this.guidance = 3.5;
      this.strength = 0.1;
    } else if (type === NodeType.VIDEO) {
      this.type = NodeType.VIDEO;
      this.name = 'Video';
    } else if (type === NodeType.THREE_D) {
      this.type = NodeType.THREE_D;
      this.name = '3d';
    } else if (type === NodeType.PALETTE) {
      this.type = NodeType.PALETTE;
      this.name = 'Palette';
    } else if (type === NodeType.DRAWING) {
      this.type = NodeType.DRAWING;
      this.name = 'Drawing';
      this.dominantColor = { top: '#ffffff', bottom: '#ffffff', all: '#ffffff' };
      this.guidance = 1.5;
      this.strength = 0.75;
    } else if (type === NodeType.PALETTE) {
      this.type = NodeType.PALETTE;
      this.name = 'Palette';
    } else if (type === NodeType.DRAWING) {
      this.type = NodeType.DRAWING;
      this.name = 'Drawing';
    } else {
      throw new Error('Invalid node type :' + type);
    }
    return this;
  }

  setPos(pos: { x: number; y: number }, center: boolean = true) {
    if (this.type === NodeType.ARTIFACTS) {
      this.x = pos.x - (center ? NodeArtifactsConstant.width / 2 : 0);
      this.y = pos.y - (center ? NodeArtifactsConstant.height / 2 : 0);
    } else if (this.type === NodeType.PLACEHOLDER) {
      this.x = pos.x - (center ? NodePlaceholderConstant.width / 2 : 0);
      this.y = pos.y - (center ? NodePlaceholderConstant.height / 2 : 0);
    } else {
      this.x = pos.x - (center ? 300 / 2 : 0);
      this.y = pos.y - (center ? 400 / 2 : 0);
    }
    return this;
  }

  rePos() {
    const point = {
      x: this.x + NodePlaceholderConstant.width / 2,
      y: this.y + NodeArtifactsConstant.height / 2,
    };
    return this.setPos(point);
  }

  setVisible(value = true) {
    this.visible = value;
    return this;
  }

  setPlaceHolder(srcNode?: NodeItem) {
    // if (srcNode) {
    //   this.source.push(srcNode.id);
    // }
    this.type = NodeType.PLACEHOLDER;
    this.visible = true;
    this.name = 'Placeholder';
    return this;
  }

  toApiNode(project_id: number) {
    const apiNode = new Api.Node();
    apiNode.id = Number(this.id);
    apiNode.name = this.name;
    apiNode.style = this.type;
    apiNode.pos_x = this.x;
    apiNode.pos_y = this.y;
    apiNode.project_id = project_id;
    apiNode.text = this.text;
    apiNode.model = this.model;
    // apiNode.content = this.content;
    apiNode.options = {
      artifact_id: this.artifact_id,
      artifact: this.artifact,
      tags: this.tags,
      visible: this.visible,
      prompt: this.prompt,
      state: this.state,
      sizeText: this.sizeText,
      background: this.background,
      raw: this.raw,
      camera: this.camera,
      screenshot: this.screenshot,
      guidance: this.guidance,
      strength: this.strength,
      imageStyle: this.imageStyle,
      textureMesh: this.textureMesh,
      material: this.material,
      tier: this.tier,
      topology: this.topology,
    };
    apiNode.sources = this.source.map((id) => Number(id));
    apiNode.targets = this.target.map((id) => Number(id));
    apiNode.model = this.model;
    return apiNode;
  }

  canEdit() {
    const editableTypes = [NodeType.IMAGE, NodeType.VIDEO, NodeType.THREE_D];
    return editableTypes.indexOf(this.type) !== -1 && this.content;
  }

  canConnect(target: NodeItem) {
    if (target.type === NodeType.ARTIFACTS) {
      return false;
    } else if (target.type === NodeType.DRAWING) {
      return this.matchType([NodeType.ARTIFACTS, NodeType.PROMPT, NodeType.IMAGE]);
    } else if (target.type === NodeType.IMAGE) {
      return this.matchType([
        NodeType.ARTIFACTS,
        NodeType.PROMPT,
        NodeType.IMAGE,
        NodeType.THREE_D,
        NodeType.PALETTE,
        NodeType.DRAWING,
      ]);
    } else if (target.type === NodeType.PLACEHOLDER) {
      return true;
    } else if (target.type === NodeType.PALETTE) {
      return this.matchType([NodeType.IMAGE]);
    } else if (target.type === NodeType.PROMPT) {
      return this.matchType([
        NodeType.ARTIFACTS,
        NodeType.IMAGE,
        NodeType.THREE_D,
        NodeType.PALETTE,
      ]);
    } else if (target.type === NodeType.THREE_D) {
      return this.matchType([NodeType.IMAGE, NodeType.PROMPT]);
    } else if (target.type === NodeType.VIDEO) {
      return this.matchType([NodeType.IMAGE, NodeType.ARTIFACTS, NodeType.PROMPT]);
    } else {
      return false;
    }
  }

  private matchType(types: NodeType[]) {
    return types.indexOf(this.type) !== -1;
  }

  getSize(): { width: number; height: number } {
    if (this.type === NodeType.PALETTE) {
      return { width: 300, height: 300 };
    }
    const sizeMap: { [key: string]: { width: number; height: number } } = {
      '1:1': { width: 300, height: 300 },
      '4:3': { width: 400, height: 300 },
      '3:4': { width: 300, height: 400 },
      '3:2': { width: 300, height: 200 },
      '2:3': { width: 200, height: 300 },
      '16:9': { width: 562, height: 375 },
      '9:16': { width: 375, height: 562 },
      '21:9': { width: 875, height: 375 },
      '9:21': { width: 375, height: 875 },
    };

    return sizeMap[this.sizeText] || { width: 300, height: 400 };
  }

  startOperation() {
    this.abortController = new AbortController();
    this.state = 'loading';
    return this.abortController.signal;
  }

  cancelOperation() {
    if (this.abortController && !this.abortController.signal.aborted) {
      this.abortController.abort();
      this.state = 'revoked';
    }
    this.abortController = undefined;
  }

  isOperationInProgress(): boolean {
    return this.state === 'loading' && !!this.abortController;
  }
}
